import { Breakpoints, Dimensions } from './hooks.types';
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';

export default function useScreenSize(): {
  dimensions: Dimensions;
  breakpoints: Breakpoints;
  isWindowSmaller: (breakpoint: keyof Breakpoints) => boolean;
  isWindowBigger: (breakpoint: keyof Breakpoints) => boolean;
  isResizing: boolean;
} {
  const theme = useTheme();
  const [dimensions, setDimensions] = useState<Dimensions>({
    height: window.innerHeight,
    width: window.innerWidth,
  });
  const [isResizing, setIsResizing] = useState<boolean>(false);

  const breakpoints: Breakpoints = {
    xs: theme.breakpoints.values.xs,
    sm: theme.breakpoints.values.sm,
    md: theme.breakpoints.values.md,
    lg: theme.breakpoints.values.lg,
    xl: theme.breakpoints.values.xl,
  };

  const isWindowSmaller = (breakpoint: keyof Breakpoints): boolean => {
    return dimensions.width < breakpoints[breakpoint];
  };

  const isWindowBigger = (breakpoint: keyof Breakpoints): boolean => {
    return dimensions.width >= breakpoints[breakpoint];
  };

  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });

      setIsResizing(false);
    }, 500);

    const trackResize = () => {
      setIsResizing(true);
      debouncedHandleResize();
    };

    window.addEventListener('resize', trackResize);

    return () => {
      window.removeEventListener('resize', trackResize);
    };
  }, [setIsResizing]);

  return { dimensions, breakpoints, isWindowSmaller, isWindowBigger, isResizing };
}
